import { useMemo, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import spec from "@/apidoc/openapi.json";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { FullscreenLoader } from "@/components/FullscreenLoader";

export default function ApiDoc() {
  const systemRef = useRef<any>(null);
  const { user, loading: authLoading } = useAuth();

  const { data: isPublic, isLoading: settingLoading } = useQuery({
    queryKey: ["settings", "apidoc_public_access"],
    queryFn: async () => {
      const { data } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "apidoc_public_access")
        .maybeSingle();
      // Default to true (public) if setting doesn't exist
      if (!data) return true;
      return data.value === true || data.value === "true";
    },
    staleTime: 5 * 60 * 1000,
  });

  const dynamicSpec = useMemo(() => ({
    ...spec,
    servers: [
      {
        url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`,
        description: "Backend",
      },
    ],
  }), []);

  const onComplete = useCallback((system: any) => {
    systemRef.current = system;
  }, []);

  const responseInterceptor = useCallback((response: any) => {
    try {
      if (
        response.ok &&
        response.url &&
        (response.url.includes("/auth/login") || response.url.includes("/auth/signup"))
      ) {
        const body = typeof response.body === "string" ? JSON.parse(response.body) : response.body;
        const token = body?.session?.access_token;
        if (token && systemRef.current) {
          systemRef.current.preauthorizeApiKey("bearerAuth", token);
        }
      }
    } catch {
      // silently ignore parsing errors
    }
    return response;
  }, []);

  if (settingLoading || authLoading) {
    return <FullscreenLoader />;
  }

  // If not public and user is not logged in, redirect to login
  if (isPublic === false && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <SwaggerUI
        spec={dynamicSpec}
        onComplete={onComplete}
        responseInterceptor={responseInterceptor}
      />
    </div>
  );
}
